import {
  AdhaUaePassErrorCode,
  AdhaUaePassErrorDescription,
  type AdhaUaePassMappedError,
} from '@/modules/auth/constants/adhaUaePassError';
import { enTranslations } from '@/core/constants/translations/en';
import type { FlatTranslationKey } from '@/core/types/i18n';

const EN_ADHA_UAE_PASS = enTranslations.auth.adhaUaePass;

export const ADHA_UAE_PASS_MESSAGE_KEYS = {
  USER_CANCELLED_LOGIN: 'auth.adhaUaePass.userCancelledLogin',
  SOMETHING_WENT_WRONG: 'auth.adhaUaePass.somethingWentWrong',
  SOP1: 'auth.adhaUaePass.sop1',
  MISSING_CODE: 'auth.adhaUaePass.missingCode',
  USER_NOT_REGISTERED: 'auth.adhaUaePass.userNotRegistered',
  ERROR_INVALID_SESSION: 'auth.adhaUaePass.errorInvalidSession',
  ERROR_GENERIC: 'auth.adhaUaePass.errorGeneric',
} as const satisfies Record<string, FlatTranslationKey>;

const LABEL_BY_KEY: Partial<Record<FlatTranslationKey, string>> = {
  [ADHA_UAE_PASS_MESSAGE_KEYS.USER_CANCELLED_LOGIN]:
    EN_ADHA_UAE_PASS.userCancelledLogin,
  [ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG]:
    EN_ADHA_UAE_PASS.somethingWentWrong,
  [ADHA_UAE_PASS_MESSAGE_KEYS.SOP1]: EN_ADHA_UAE_PASS.sop1,
  [ADHA_UAE_PASS_MESSAGE_KEYS.MISSING_CODE]: EN_ADHA_UAE_PASS.missingCode,
  [ADHA_UAE_PASS_MESSAGE_KEYS.USER_NOT_REGISTERED]:
    EN_ADHA_UAE_PASS.userNotRegistered,
  [ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_INVALID_SESSION]:
    EN_ADHA_UAE_PASS.errorInvalidSession,
  [ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_GENERIC]: EN_ADHA_UAE_PASS.errorGeneric,
};

/** English fallback when no `t` is available (e.g. service layer). */
export function adhaUaePassMessageKeyToLabel(
  key: FlatTranslationKey,
): string {
  return LABEL_BY_KEY[key] ?? EN_ADHA_UAE_PASS.somethingWentWrong;
}

function normalizeErrorCode(errorCode: string): string {
  return errorCode.trim().toLowerCase().replaceAll('-', '_');
}

function messageKeyForErrorCode(normalizedCode: string): AdhaUaePassMappedError {
  switch (normalizedCode) {
    case 'cancelledonapp':
    case 'cancelled_on_app':
      return {
        code: AdhaUaePassErrorCode.CANCELLED_ON_APP,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.USER_CANCELLED_LOGIN,
      };
    case 'access_denied':
      return {
        code: AdhaUaePassErrorCode.ACCESS_DENIED,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.USER_CANCELLED_LOGIN,
      };
    case 'invalid_request':
    case 'login_required':
    case 'profile_incomplete':
      return {
        code:
          normalizedCode === 'profile_incomplete'
            ? AdhaUaePassErrorCode.PROFILE_INCOMPLETE
            : AdhaUaePassErrorCode.INVALID_REQUEST,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG,
      };
    case 'missing_code':
      return {
        code: AdhaUaePassErrorCode.MISSING_CODE,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.MISSING_CODE,
      };
    case 'sop1':
    case 'sop_1':
      return {
        code: AdhaUaePassErrorCode.SOP1,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.SOP1,
      };
    case 'user_not_registered':
      return {
        code: AdhaUaePassErrorCode.USER_NOT_REGISTERED,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.USER_NOT_REGISTERED,
      };
    default:
      return {
        code: normalizedCode || AdhaUaePassErrorCode.INVALID_REQUEST,
        messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG,
      };
  }
}

/**
 * Map UAE Pass / BFF error to UI.
 * Priority: known `error_description` → then `error` / login API `code`.
 * Message keys always come from `auth.adhaUaePass` — never from API `errorMessage`.
 */
export function mapAdhaUaePassError(
  errorCode?: string,
  errorDescription?: string,
): AdhaUaePassMappedError {
  const description = errorDescription?.trim() ?? '';

  if (
    description === AdhaUaePassErrorDescription.WEB ||
    description === AdhaUaePassErrorDescription.APP
  ) {
    return {
      code: AdhaUaePassErrorCode.CANCELLED_ON_APP,
      messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.USER_CANCELLED_LOGIN,
    };
  }

  if (errorCode?.trim()) {
    return messageKeyForErrorCode(normalizeErrorCode(errorCode));
  }

  return {
    code: AdhaUaePassErrorCode.INVALID_REQUEST,
    messageKey: ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG,
  };
}

function readStringField(
  source: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readMappedErrorCode(
  response: Record<string, unknown>,
): string | undefined {
  const sources: Record<string, unknown>[] = [response];
  for (const key of ['data', 'details']) {
    const nested = response[key];
    if (nested && typeof nested === 'object') {
      sources.push(nested as Record<string, unknown>);
    }
  }

  for (const source of sources) {
    const mappedCode =
      readStringField(source, 'code') ||
      readStringField(source, 'errorCode') ||
      readStringField(source, 'error');
    if (mappedCode) {
      return mappedCode;
    }
  }

  return undefined;
}

export function resolveAdhaUaePassUserMessageKey(
  err: unknown,
): FlatTranslationKey {
  if (!err || typeof err !== 'object') {
    return ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG;
  }

  const withResponse = err as { response?: unknown };
  const response =
    withResponse.response && typeof withResponse.response === 'object'
      ? (withResponse.response as Record<string, unknown>)
      : null;

  const mappedCode = response ? readMappedErrorCode(response) : undefined;
  const errorDescription =
    (response && readStringField(response, 'error_description')) || undefined;

  if (!mappedCode && !errorDescription) {
    return ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG;
  }

  const mapped = mapAdhaUaePassError(mappedCode, errorDescription);
  return mapped.messageKey ?? ADHA_UAE_PASS_MESSAGE_KEYS.SOMETHING_WENT_WRONG;
}

type AdhaUaePassTranslateFn = (key: FlatTranslationKey) => string;

/**
 * Resolve a thrown/API error to user-facing copy.
 * ADHA login API shape: `{ errorCode, errorMessage, code }` — map by code fields only.
 */
export function resolveAdhaUaePassUserMessage(
  err: unknown,
  t?: AdhaUaePassTranslateFn,
): string {
  const messageKey = resolveAdhaUaePassUserMessageKey(err);
  if (t) {
    return t(messageKey);
  }
  return adhaUaePassMessageKeyToLabel(messageKey);
}
